<template>
  <Button
    label="Export Equipment Data"
    icon="pi pi-file-excel"
    class="col-fixed"
    :disabled="hasSizings"
    @click="exportToExcel"
  />
</template>

<script setup lang="ts">
import Button from "primevue/button";
import * as XLSX from "xlsx";
import type { EquipmentItem } from "@/views/DataAnalytics/models/EquipmentSelection";
import type { Sizings_Bool_Exp } from "gql/api";
import type { Sizing } from "@/composables/forms/formTypes";
import { orderScenariosBySizingOrder } from "@/utils/scenarioUtils";
import { computed } from "vue";

// Props to accept data from DataAnalytics
// Define the props to accept data from DataAnalytics
const props = defineProps<{
  sizings: Sizing[];
  sizingsWhere: Sizings_Bool_Exp | undefined;
  aggregatedCounts: {
    sizingsCount: number;
    scenariosCount: number;
    performanceEvaluationsCount: number;
  };
  selectedEquipmentMap: Record<string, EquipmentItem & { type: string }>;
}>();

const hasSizings = computed(() => props.aggregatedCounts.sizingsCount === 0);

// Use the imported props in the component
const { sizings, sizingsWhere, aggregatedCounts, selectedEquipmentMap } = props; // eslint-disable-line @typescript-eslint/no-unused-vars

// Enums and types
enum EquipSelectEnum {
  Casings = "casings",
  Tubings = "tubings",
  Intakes = "intakes",
  Cables = "cables",
  Pumps = "pumps",
  Motors = "motors",
  Seals = "seals"
}

// Functions related to equipment formatting
const formatEquipmentByType = (item: EquipmentItem, type: string): string => {
  switch (type) {
    case EquipSelectEnum.Casings:
      if (item.metric_name) {
        return `${item.metric_name}, ${item.imperial_name}`;
      }
      return `${item.imperial_name}, ${item.metric_name}`;
    case EquipSelectEnum.Tubings:
      if (item.metric_name) {
        return `${item.metric_name}, ${item.imperial_name}`;
      }
      return `${item.imperial_name}, ${item.metric_name}`;
    case EquipSelectEnum.Intakes:
      return `${item.manufacturer} / ${item.intake_type}`;
    case EquipSelectEnum.Cables:
      return `${item.manufacturer} / ${item.gauge} gauge / ${item.kv} kv / ${item.construction}`;
    case EquipSelectEnum.Pumps:
      return `${item.manufacturer} / ${item.series_model}`;
    case EquipSelectEnum.Motors:
      return `${item.manufacturer} / ${item.part_number}`;
    case EquipSelectEnum.Seals:
      return `${item.manufacturer} / ${item.outer_diameter_inches} / ${item.configuration}`;
    default:
      return "";
  }
};

// Export to Excel method
const exportToExcel = () => {
  const dataToExport = prepareDataForExport();
  const workbook = XLSX.utils.book_new();

  Object.keys(dataToExport).forEach(sheetName => {
    const ws = XLSX.utils.json_to_sheet(dataToExport[sheetName]);
    XLSX.utils.book_append_sheet(workbook, ws, sheetName);
  });

  XLSX.writeFile(workbook, "EquipmentData.xlsx");
};

const prepareDataForExport = () => {
  const data: Record<string, unknown[]> = {};
  const searchParametersSummary = getSearchParametersSummary();

  const summaryData: Array<Record<string, any>> = [];

  // 3) Add row about "Scenarios Returned" (example)
  summaryData.push({
    Parameter: "Scenarios Returned",
    "Equipment 1": props.aggregatedCounts.scenariosCount
  });

  // 4) If the user wants a blank line
  summaryData.push({
    Parameter: "",
    "Equipment 1": ""
  });

  // ----- 4a) Handle SEARCH PARAMETERS -----
  if (
    Array.isArray(searchParametersSummary["SEARCH PARAMETERS"]) &&
    searchParametersSummary["SEARCH PARAMETERS"].length > 0
  ) {
    // Label row
    summaryData.push({
      Parameter: "SEARCH PARAMETERS"
    });

    searchParametersSummary["SEARCH PARAMETERS"].forEach((spItem: any) => {
      summaryData.push({
        Parameter: spItem.parameter,
        "Equipment 1": spItem.value
      });
    });
  }

  if (
    Array.isArray(searchParametersSummary["EQUIPMENT PARAMETERS"]) &&
    searchParametersSummary["EQUIPMENT PARAMETERS"].length > 0
  ) {
    summaryData.push({
      Parameter: "",
      "Equipment 1": ""
    });

    // Label row
    summaryData.push({
      Parameter: "EQUIPMENT PARAMETERS"
    });

    searchParametersSummary["EQUIPMENT PARAMETERS"].forEach((eqItem: any) => {
      const row: Record<string, any> = {
        Parameter: eqItem.parameter
      };
      if (Array.isArray(eqItem.values)) {
        eqItem.values.forEach((val: string, index: number) => {
          row[`Equipment ${index + 1}`] = val;
        });
      }
      summaryData.push(row);
    });
  }

  data.Summary = summaryData;

  const intakeData = getIntakeData();
  if (intakeData) {
    data.Intakes = intakeData;
  }

  const pumpDetailsData = getPumpDetails();
  if (pumpDetailsData) {
    data.Pumps = pumpDetailsData;
  }

  const motorData = getMotorData();
  if (motorData) {
    data.Motors = motorData;
  }

  const mleData = getMLEData();
  if (mleData) {
    data.MLEs = mleData;
  }

  const cableData = getCableData();
  if (cableData) {
    data.Cables = cableData;
  }

  const sealData = getSealData();
  if (sealData) {
    data.Seals = sealData;
  }

  return data;
};

const getIntakeData = () => {
  const intakeDetails: Record<string, { Intake: string; Qty: number }> = {};
  props.sizings.forEach(sizing => {
    orderScenariosBySizingOrder(sizing, sizing.scenarios).forEach(scenario => {
      const equipment = scenario.equipment;
      if (equipment && equipment.r_xref_intakes) {
        equipment.r_xref_intakes.forEach(intake_xref => {
          const intakeId = intake_xref.intake_id;
          const useHighStrengthShaft = intake_xref.use_high_strength_shaft;
          const shaftType = useHighStrengthShaft ? "HS Shaft" : "STD Shaft";
          const labelBase =
            intake_xref.r_intake?.intake_type ||
            intake_xref.r_intake?.part_number ||
            "Unknown";
          const intakeLabel = `${labelBase} (${shaftType})`;
          const key = `${intakeId}-${shaftType}`;

          if (!intakeDetails[key]) {
            intakeDetails[key] = {
              Intake: intakeLabel,
              Qty: 0
            };
          }
          intakeDetails[key].Qty += 1;
        });
      }
    });
  });
  if (Object.keys(intakeDetails).length === 0) {
    return null;
  }

  // Convert the details to an array
  return Object.values(intakeDetails);
};

const getPumpDetails = () => {
  const pumpDetails: Record<
    string,
    {
      "Pump Label": string;
      Qty: number;
      "Stage Count": number;
      "Housing Count": Record<string, number>;
    }
  > = {};

  props.sizings.forEach(sizing => {
    orderScenariosBySizingOrder(sizing, sizing.scenarios).forEach(scenario => {
      const equipment = scenario.equipment;
      if (equipment && equipment.r_xref_pumps) {
        equipment.r_xref_pumps.forEach(pump_xref => {
          const pumpId = pump_xref.pump_id;
          const pumpLabelBase = `${pump_xref.r_pump?.manufacturer || "Unknown"} / ${
            pump_xref.r_pump?.series_model || "Unknown"
          }`;
          const useHighStrengthShaft = pump_xref.use_high_strength_shaft;
          const shaftType = useHighStrengthShaft ? "HS Shaft" : "STD Shaft";
          const pumpLabel = `${pumpLabelBase} (${shaftType})`;
          const key = `${pumpId}-${shaftType}`;
          const totalStages = pump_xref.total_stages || 0;
          const housings = pump_xref.r_xref_housings || [];
          if (!pumpDetails[key]) {
            pumpDetails[key] = {
              "Pump Label": pumpLabel,
              Qty: 0,
              "Stage Count": 0,
              "Housing Count": {}
            };
          }
          pumpDetails[key].Qty += 1;
          pumpDetails[key]["Stage Count"] += totalStages;
          housings.forEach(housing_xref => {
            const housingNumber =
              housing_xref.r_housing?.housing_number || "Unknown";
            if (!pumpDetails[key]["Housing Count"][housingNumber]) {
              pumpDetails[key]["Housing Count"][housingNumber] = 0;
            }
            pumpDetails[key]["Housing Count"][housingNumber] += 1;
          });
        });
      }
    });
  });

  if (Object.keys(pumpDetails).length === 0) {
    return null;
  }
  return Object.values(pumpDetails).map(detail => {
    const housingCounts = Object.entries(detail["Housing Count"])
      .map(([housingNumber, count]) => `Housing ${housingNumber}: ${count}`)
      .join(", ");
    return {
      "Pump Label": detail["Pump Label"],
      Qty: detail.Qty,
      "Stage Count": detail["Stage Count"],
      "Housing Count": housingCounts
    };
  });
};

const getMotorData = () => {
  const motorGroups: Record<
    string,
    {
      Manufacturer: string;
      "Part Number": string;
      "Nameplate HP": number;
      "Nameplate Voltage": number;
      "Nameplate Current": number;
      "Length (inches)": number;
      "Outer Diameter (inches)": number;
      Qty: number;
    }
  > = {};

  props.sizings.forEach(sizing => {
    orderScenariosBySizingOrder(sizing, sizing.scenarios).forEach(scenario => {
      const equipment = scenario.equipment;
      if (equipment && equipment.r_motor) {
        const motor = equipment.r_motor;
        const motorId = motor.id;
        if (!motorGroups[motorId]) {
          motorGroups[motorId] = {
            Manufacturer: motor.manufacturer || "",
            "Part Number": motor.part_number || "",
            "Nameplate HP": motor.nameplate_horsepower || 0,
            "Nameplate Voltage": motor.nameplate_voltage || 0,
            "Nameplate Current": motor.nameplate_current || 0,
            "Length (inches)": motor.length_inches || 0,
            "Outer Diameter (inches)": motor.outer_diameter_inches || 0,
            Qty: 0
          };
        }
        motorGroups[motorId].Qty += 1;
      }
    });
  });

  if (Object.keys(motorGroups).length === 0) {
    return null;
  }

  return Object.values(motorGroups);
};

const getMLEData = () => {
  const mleGroups: Record<
    string,
    {
      Manufacturer: string;
      "Part Number": string;
      Family: string;
      Series: string;
      Gauge: string;
      "KV Rating": string;
      "Length (feet)": number;
      "Max Temperature (F)": string;
      "Resistance per 1000 ft": string;
      "Impedance per 1000 ft": string;
      Qty: number;
    }
  > = {};

  props.sizings.forEach(sizing => {
    orderScenariosBySizingOrder(sizing, sizing.scenarios).forEach(scenario => {
      const mle = scenario.equipment?.r_mle;
      if (mle) {
        const mleId = mle.id;
        if (!mleGroups[mleId]) {
          mleGroups[mleId] = {
            Manufacturer: mle.manufacturer || "",
            "Part Number": mle.part_number || "",
            Family: mle.family || "",
            Series: String(mle.series) || "",
            Gauge: String(mle.gauge) || "",
            "KV Rating": String(mle.kv) || "",
            "Length (feet)": mle.length_feet || 0,
            "Max Temperature (F)": String(mle.max_temperature_fahrenheit) || "",
            "Resistance per 1000 ft": mle.resistance_per_1000_ft || "",
            "Impedance per 1000 ft": mle.impedance_60_hz_per_1000_ft || "",
            Qty: 0
          };
        }
        mleGroups[mleId].Qty += 1;
      }
    });
  });

  if (Object.keys(mleGroups).length === 0) {
    return null;
  }

  return Object.values(mleGroups);
};

const getCableData = () => {
  const cableGroups: Record<
    string,
    { Label: string; "Total Length (feet)": number; Qty: number }
  > = {};

  props.sizings.forEach(sizing => {
    orderScenariosBySizingOrder(sizing, sizing.scenarios).forEach(scenario => {
      const equipment = scenario.equipment;
      if (equipment && equipment.r_xref_cables) {
        equipment.r_xref_cables.forEach(cable_xref => {
          const cable = cable_xref.r_cable;
          if (cable) {
            const cableId = cable.id;
            const label = `${cable.manufacturer || "Unknown"} / ${cable.gauge || "Unknown"} gauge / ${cable.kv || "Unknown"} kv / ${cable.construction || "Unknown"}`;
            const length = cable_xref.length_feet || 0;
            if (!cableGroups[cableId]) {
              cableGroups[cableId] = {
                Label: label,
                "Total Length (feet)": 0,
                Qty: 0
              };
            }
            cableGroups[cableId]["Total Length (feet)"] += length;
            cableGroups[cableId].Qty += 1;
          }
        });
      }
    });
  });

  if (Object.keys(cableGroups).length === 0) {
    return null;
  }

  return Object.values(cableGroups);
};
const getSealData = () => {
  const sealDetails: Record<string, { Seal: string; Qty: number }> = {};
  props.sizings.forEach(sizing => {
    orderScenariosBySizingOrder(sizing, sizing.scenarios).forEach(scenario => {
      const equipment = scenario.equipment;
      if (equipment && equipment.r_xref_seals) {
        equipment.r_xref_seals.forEach(seal_xref => {
          const sealId = seal_xref.seal_id;
          const useHighStrengthShaft = seal_xref.use_high_strength_shaft;
          const shaftType = useHighStrengthShaft ? "HS Shaft" : "STD Shaft";
          let baseLabel = "Unknown Seal";
          if (seal_xref.r_seal) {
            baseLabel = formatEquipmentByType(
              seal_xref.r_seal as unknown as EquipmentItem,
              EquipSelectEnum.Seals
            );
          }
          const sealLabel = `${baseLabel} (${shaftType})`;
          const key = `${sealId}-${shaftType}`;

          if (!sealDetails[key]) {
            sealDetails[key] = { Seal: sealLabel, Qty: 0 };
          }
          sealDetails[key].Qty += 1;
        });
      }
    });
  });
  if (Object.keys(sealDetails).length === 0) {
    return null;
  }
  return Object.values(sealDetails);
};

const getSearchParametersSummary = (): Record<string, any> => {
  const summary: Record<string, any> = {
    "SEARCH PARAMETERS": [],
    "EQUIPMENT PARAMETERS": []
  };

  const sizingsWhere = props.sizingsWhere;
  const nameCondition = sizingsWhere?._and?.find((condition: any) =>
    condition._or?.some((cond: any) => cond.title || cond.parent_sizing?.title)
  );
  const name =
    nameCondition?._or
      ?.find((cond: any) => cond.title)
      ?.title?._ilike?.replace(/%/g, "") ||
    nameCondition?._or
      ?.find((cond: any) => cond.parent_sizing)
      ?.parent_sizing?.title?._ilike?.replace(/%/g, "");

  if (name) {
    summary["SEARCH PARAMETERS"].push({
      parameter: "Name",
      value: name
    });
  }

  const ownerCondition = sizingsWhere?._and?.find((condition: any) =>
    condition._or?.some(
      (cond: any) => cond.author || cond.imported_from_sizing?.legacy_user
    )
  );
  const owner =
    ownerCondition?._or
      ?.find((cond: any) => cond.author)
      ?.author?.display_name?._ilike?.replace(/%/g, "") ||
    ownerCondition?._or
      ?.find((cond: any) => cond.imported_from_sizing)
      ?.imported_from_sizing?.legacy_user?.display_name?._ilike?.replace(
        /%/g,
        ""
      );

  if (owner) {
    summary["SEARCH PARAMETERS"].push({
      parameter: "Owner",
      value: owner
    });
  }

  const well = sizingsWhere?._and
    ?.find((condition: any) => condition.well)
    ?.well?.well_name?._ilike?.replace(/%/g, "");
  if (well) {
    summary["SEARCH PARAMETERS"].push({
      parameter: "Well",
      value: well
    });
  }

  const customer = sizingsWhere?._and
    ?.find((condition: any) => condition.customer)
    ?.customer?.customer_name?._ilike?.replace(/%/g, "");
  if (customer) {
    summary["SEARCH PARAMETERS"].push({
      parameter: "Customer",
      value: customer
    });
  }

  const dateCondition = sizingsWhere?._and?.find((condition: any) =>
    condition._and?.some((cond: any) => cond.journal_updated_at)
  );
  const dateGte = dateCondition?._and?.find(
    (cond: any) => cond.journal_updated_at?._gte
  )?.journal_updated_at?._gte;
  const dateLte = dateCondition?._and?.find(
    (cond: any) => cond.journal_updated_at?._lte
  )?.journal_updated_at?._lte;
  if (dateGte && dateLte) {
    summary["SEARCH PARAMETERS"].push({
      parameter: "Date Between",
      value: `${new Date(dateGte).toLocaleDateString()} - ${new Date(
        dateLte
      ).toLocaleDateString()}`
    });
  }

  const equipmentFilters = sizingsWhere?.scenarios?.equipment || {};
  const wellProperties = sizingsWhere?.scenarios?.well_properties || {};

  const getEquipmentLabels = (ids: any[], equipmentType: string) => {
    return ids.map(id => {
      const item = props.selectedEquipmentMap[id];
      if (item) {
        return formatEquipmentByType(item, equipmentType);
      } else {
        console.warn(`Equipment item not found for ID: ${id}`);
        return id; // fallback to ID if item not found
      }
    });
  };

  const pumpIds = equipmentFilters?.xref_pumps?.pump_id?._in || [];
  if (pumpIds.length > 0) {
    const pumpNames = getEquipmentLabels(pumpIds, EquipSelectEnum.Pumps);
    summary["EQUIPMENT PARAMETERS"].push({
      parameter: "Pumps",
      values: pumpNames
    });
  }

  // INTAKES
  const intakeIds = equipmentFilters?.xref_intakes?.intake_id?._in || [];
  if (intakeIds.length > 0) {
    const intakeNames = getEquipmentLabels(intakeIds, EquipSelectEnum.Intakes);
    summary["EQUIPMENT PARAMETERS"].push({
      parameter: "Intakes",
      values: intakeNames
    });
  }

  // MOTORS
  const motorIds = equipmentFilters?.motor_id?._in || [];
  if (motorIds.length > 0) {
    const motorNames = getEquipmentLabels(motorIds, EquipSelectEnum.Motors);
    summary["EQUIPMENT PARAMETERS"].push({
      parameter: "Motors",
      values: motorNames
    });
  }

  // CABLES
  const cableIds = equipmentFilters?.xref_cables?.cable_id?._in || [];
  if (cableIds.length > 0) {
    const cableNames = getEquipmentLabels(cableIds, EquipSelectEnum.Cables);
    summary["EQUIPMENT PARAMETERS"].push({
      parameter: "Cables",
      values: cableNames
    });
  }

  // SEALS
  const sealIds = equipmentFilters?.xref_seals?.seal_id?._in || [];
  if (sealIds.length > 0) {
    const sealNames = getEquipmentLabels(sealIds, EquipSelectEnum.Seals);
    summary["EQUIPMENT PARAMETERS"].push({
      parameter: "Seals",
      values: sealNames
    });
  }

  // Bottom CASINGS
  const casingIds = wellProperties?.bottom_casing_id?._in || [];
  if (casingIds.length > 0) {
    const casingNames = getEquipmentLabels(casingIds, EquipSelectEnum.Casings);
    summary["EQUIPMENT PARAMETERS"].push({
      parameter: "Bottom Casings",
      values: casingNames
    });
  }
  // Top CASINGS
  const topCasingIds = wellProperties?.top_casing_id?._in || [];
  if (topCasingIds.length > 0) {
    const topCasingNames = getEquipmentLabels(
      topCasingIds,
      EquipSelectEnum.Casings
    );
    summary["EQUIPMENT PARAMETERS"].push({
      parameter: "Top Casings",
      values: topCasingNames
    });
  }

  // Top Tubings
  const tubingIds = wellProperties?.top_tubing_id?._in || [];
  if (tubingIds.length > 0) {
    const tubingNames = getEquipmentLabels(tubingIds, EquipSelectEnum.Tubings);
    summary["EQUIPMENT PARAMETERS"].push({
      parameter: "Top Tubings",
      values: tubingNames
    });
  }
  // Bottom Tubings
  const bottomTubingIds = wellProperties?.bottom_tubing_id?._in || [];
  if (bottomTubingIds.length > 0) {
    const bottomTubingNames = getEquipmentLabels(
      bottomTubingIds,
      EquipSelectEnum.Tubings
    );
    summary["EQUIPMENT PARAMETERS"].push({
      parameter: "Bottom Tubings",
      values: bottomTubingNames
    });
  }
  return summary;
};
</script>
